import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { CheckCircle, Clock, DollarSign, User, MapPin, Calendar, Plus, Minus } from 'lucide-react';

const JobManagement = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('immediate_escrow');
  const [milestones, setMilestones] = useState([]);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionData, setCompletionData] = useState({
    notes: '',
    images: []
  });

  useEffect(() => {
    if (user) {
      fetchJobs();
    }
  }, [user]);

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const endpoint = user.userType === 'customer' 
        ? '/api/jobs/my-jobs' 
        : '/api/jobs/assigned-jobs';
        
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setJobs(Array.isArray(data) ? data : []);
      } else if (response.status === 403) {
        console.warn('Access denied - user may not have proper permissions');
        setJobs([]);
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch jobs:', errorData.detail || 'Unknown error');
        setJobs([]);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobBids = async (jobId) => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/bids`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBids(Array.isArray(data) ? data : []);
      } else if (response.status === 403) {
        alert('Access denied - only job owner can view bids');
        setBids([]);
      } else if (response.status === 404) {
        alert('Job not found');
        setBids([]);
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to load bids');
        setBids([]);
      }
    } catch (error) {
      console.error('Error fetching bids:', error);
      alert('Failed to load bids');
      setBids([]);
    }
  };

  const addMilestone = () => {
    setMilestones([...milestones, {
      title: '',
      description: '',
      percentage: 0,
      dueDate: ''
    }]);
  };

  const removeMilestone = (index) => {
    setMilestones(milestones.filter((_, i) => i !== index));
  };

  const updateMilestone = (index, field, value) => {
    const updated = milestones.map((milestone, i) => 
      i === index ? { ...milestone, [field]: value } : milestone
    );
    setMilestones(updated);
  };

  const acceptBid = async () => {
    if (!selectedBid) return;

    try {
      const token = localStorage.getItem('dcTech_token');
      
      // Validate milestones if using milestone payment
      if (paymentMethod === 'milestones') {
        const totalPercentage = milestones.reduce((sum, m) => sum + parseFloat(m.percentage || 0), 0);
        if (Math.abs(totalPercentage - 100) > 0.01) {
          alert('Milestone percentages must sum to 100%');
          return;
        }
        
        if (milestones.some(m => !m.title || !m.description || !m.percentage)) {
          alert('Please fill in all milestone details');
          return;
        }
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${selectedJob._id}/accept-bid`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bidId: selectedBid._id,
          paymentMethod: paymentMethod,
          milestones: paymentMethod === 'milestones' ? milestones : []
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Redirect to Stripe checkout
        if (data.paymentUrl) {
          window.location.href = data.paymentUrl;
        }
        
        setShowAcceptModal(false);
        fetchJobs();
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to accept bid');
      }
    } catch (error) {
      console.error('Error accepting bid:', error);
    }
  };

  const markJobComplete = async () => {
    if (!selectedJob) return;

    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${selectedJob._id}/mark-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          completionNotes: completionData.notes,
          completionImages: completionData.images
        }),
      });

      if (response.ok) {
        setShowCompletionModal(false);
        setCompletionData({ notes: '', images: [] });
        fetchJobs();
        alert('Job marked as complete! Customer will be notified.');
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to mark job complete');
      }
    } catch (error) {
      console.error('Error marking job complete:', error);
    }
  };

  const confirmCompletion = async (jobId) => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/confirm-completion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        fetchJobs();
        alert('Job completion confirmed! Payment has been released.');
      } else {
        const errorData = await response.json();
        alert(errorData.detail || 'Failed to confirm completion');
      }
    } catch (error) {
      console.error('Error confirming completion:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800';
      case 'assigned': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading jobs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {user.userType === 'customer' ? 'My Jobs' : 'Assigned Jobs'}
          </h1>
          <p className="text-gray-600 mt-2">
            {user.userType === 'customer' 
              ? 'Manage your job postings and track progress'
              : 'View jobs assigned to you and track payment status'
            }
          </p>
        </div>

        {jobs.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No jobs found</h3>
            <p className="text-gray-600">
              {user.userType === 'customer' 
                ? "You haven't posted any jobs yet."
                : "No jobs have been assigned to you yet."
              }
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {jobs.map((job) => (
              <div key={job._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{job.title}</h3>
                    <p className="text-gray-600 mb-4">{job.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {job.location}
                      </div>
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 mr-1" />
                        {job.budget ? `$${job.budget}` : 'Budget negotiable'}
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(job.createdAt).toLocaleDateString()}
                      </div>
                      {user.userType === 'customer' && (
                        <div className="flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {job.bidsCount} bid{job.bidsCount !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(job.status)}`}>
                      {job.status.replace('_', ' ').toUpperCase()}
                    </span>
                    
                    {job.assignedProviderName && (
                      <p className="text-sm text-gray-600 mt-2">
                        Provider: {job.assignedProviderName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Job Actions */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
                  {user.userType === 'customer' && job.status === 'open' && job.bidsCount > 0 && (
                    <button
                      onClick={() => {
                        setSelectedJob(job);
                        fetchJobBids(job._id);
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium"
                    >
                      View Bids ({job.bidsCount})
                    </button>
                  )}

                  {user.userType === 'provider' && job.status === 'assigned' && !job.providerMarkedComplete && (
                    <button
                      onClick={() => {
                        setSelectedJob(job);
                        setShowCompletionModal(true);
                      }}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium"
                    >
                      Mark Complete
                    </button>
                  )}

                  {user.userType === 'customer' && job.providerMarkedComplete && !job.customerConfirmedCompletion && (
                    <button
                      onClick={() => confirmCompletion(job._id)}
                      className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-medium"
                    >
                      Confirm Completion & Release Payment
                    </button>
                  )}

                  {job.useMilestones && job.milestones && job.milestones.length > 0 && (
                    <div className="w-full mt-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Milestones:</h4>
                      <div className="space-y-2">
                        {job.milestones.map((milestone, index) => (
                          <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                            <div>
                              <p className="font-medium">{milestone.title}</p>
                              <p className="text-sm text-gray-600">{milestone.description}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">${milestone.amount.toFixed(2)}</p>
                              <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                                milestone.status === 'completed' ? 'bg-green-100 text-green-800' :
                                milestone.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {milestone.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bids Modal */}
      {selectedJob && bids.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Bids for: {selectedJob.title}</h3>
              <p className="text-gray-600 mt-1">{bids.length} bid{bids.length !== 1 ? 's' : ''} received</p>
            </div>
            
            <div className="p-6 space-y-4">
              {bids.map((bid) => (
                <div key={bid._id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-lg">{bid.providerName}</h4>
                      <p className="text-gray-600">{bid.providerCompany}</p>
                      {bid.providerRating && (
                        <p className="text-yellow-500">★ {bid.providerRating.toFixed(1)}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">${bid.bidAmount}</p>
                      <p className="text-sm text-gray-500">{bid.estimatedDuration}</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 mb-3">{bid.description}</p>
                  
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-gray-500">
                      Available: {bid.availableDate}
                    </p>
                    
                    {bid.status === 'pending' && (
                      <button
                        onClick={() => {
                          setSelectedBid(bid);
                          setShowAcceptModal(true);
                          setPaymentMethod(bid.bidAmount >= 500 ? 'milestones' : 'immediate_escrow');
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
                      >
                        Accept Bid
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="p-6 border-t border-gray-200 text-right">
              <button
                onClick={() => {
                  setSelectedJob(null);
                  setBids([]);
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept Bid Modal */}
      {showAcceptModal && selectedBid && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Accept Bid: {selectedBid.providerName}</h3>
              <p className="text-gray-600 mt-1">Bid Amount: ${selectedBid.bidAmount}</p>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-semibold mb-3">Payment Method</h4>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="immediate_escrow"
                      checked={paymentMethod === 'immediate_escrow'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <p className="font-medium">50% Immediate + 50% Escrow</p>
                      <p className="text-sm text-gray-600">
                        Pay 50% upfront, remaining 50% held in escrow until completion
                      </p>
                    </div>
                  </label>
                  
                  {selectedBid.bidAmount >= 500 && (
                    <label className="flex items-center">
                      <input
                        type="radio"
                        value="milestones"
                        checked={paymentMethod === 'milestones'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mr-3"
                      />
                      <div>
                        <p className="font-medium">Milestone-Based Payments</p>
                        <p className="text-sm text-gray-600">
                          Break payment into project milestones (recommended for projects $500+)
                        </p>
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {paymentMethod === 'milestones' && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold">Project Milestones</h4>
                    <button
                      onClick={addMilestone}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm"
                    >
                      <Plus className="w-4 h-4 inline mr-1" />
                      Add Milestone
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {milestones.map((milestone, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-medium">Milestone {index + 1}</h5>
                          <button
                            onClick={() => removeMilestone(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <input
                            type="text"
                            placeholder="Milestone title"
                            value={milestone.title}
                            onChange={(e) => updateMilestone(index, 'title', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2"
                          />
                          <input
                            type="number"
                            placeholder="Percentage"
                            value={milestone.percentage}
                            onChange={(e) => updateMilestone(index, 'percentage', e.target.value)}
                            className="border border-gray-300 rounded px-3 py-2"
                            min="0"
                            max="100"
                          />
                        </div>
                        
                        <textarea
                          placeholder="Milestone description"
                          value={milestone.description}
                          onChange={(e) => updateMilestone(index, 'description', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2 mt-2"
                          rows="2"
                        />
                        
                        <input
                          type="date"
                          value={milestone.dueDate}
                          onChange={(e) => updateMilestone(index, 'dueDate', e.target.value)}
                          className="border border-gray-300 rounded px-3 py-2 mt-2"
                        />
                        
                        <p className="text-sm text-gray-600 mt-2">
                          Amount: ${((selectedBid.bidAmount * milestone.percentage) / 100).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {milestones.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      Total: {milestones.reduce((sum, m) => sum + parseFloat(m.percentage || 0), 0)}%
                      {Math.abs(milestones.reduce((sum, m) => sum + parseFloat(m.percentage || 0), 0) - 100) > 0.01 && (
                        <span className="text-red-500 ml-2">Must equal 100%</span>
                      )}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAcceptModal(false);
                  setSelectedBid(null);
                  setMilestones([]);
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={acceptBid}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium"
              >
                Accept & Proceed to Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold">Mark Job Complete</h3>
              <p className="text-gray-600 mt-1">{selectedJob?.title}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Completion Notes (Optional)
                </label>
                <textarea
                  value={completionData.notes}
                  onChange={(e) => setCompletionData({...completionData, notes: e.target.value})}
                  placeholder="Describe the work completed, any issues encountered, etc."
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  rows="4"
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCompletionModal(false);
                  setCompletionData({ notes: '', images: [] });
                }}
                className="px-6 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={markJobComplete}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg font-medium"
              >
                Mark Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobManagement;